import { IconType } from '../components/various/Icon/Icon';
import { RELATED_RESOURCES } from './related-resources';

/**
 * Platform page section type
 */
export type PlatformSection = 'Core Platform' | 'Storage & Integrations';

/**
 * Filter tags for the /platform page feature grid.
 * Features with no tags only appear when "All" is selected.
 */
export type PlatformFilterTag =
  | 'Video'
  | 'PDF & documents'
  | 'Share & send'
  | 'Review & compare'
  | 'Safe zones & resize';

/** Filter options for the platform page (id and label for UI) */
export const PLATFORM_FILTER_OPTIONS: { id: PlatformFilterTag; label: string }[] = [
  { id: 'Video', label: 'Video' },
  { id: 'PDF & documents', label: 'PDF & documents' },
  { id: 'Share & send', label: 'Share & send' },
  { id: 'Review & compare', label: 'Review & compare' },
  { id: 'Safe zones & resize', label: 'Safe zones & resize' },
];

/** Shown on /platform when a filter chip is active — adds crawlable copy for each category */
export const PLATFORM_FILTER_DESCRIPTIONS: Record<PlatformFilterTag, string> = {
  Video:
    'Video capabilities span review, annotation, proofing, versioning, and delivery—built for teams that live on timelines.',
  'PDF & documents':
    'PDF and document features connect markup, comparison, highlights, and approvals to the same projects as your video work.',
  'Share & send':
    'Share and send features cover links, hosting, and large-file delivery so reviewers get the right asset without friction.',
  'Review & compare':
    'Review and compare features keep feedback frame- or pixel-accurate across video, PDFs, images, and documents.',
  'Safe zones & resize':
    'Safe zones and resize tools align exports with platform rules for Shorts, Reels, TikTok, and YouTube channel art.',
};

/**
 * Platform page configuration interface
 * Defines metadata for platform feature pages used in navigation, sitemap, and related resources
 */
export interface PlatformPage {
  /** Display label for navigation */
  label: string;
  /** URL path (must match file path in pages directory) */
  href: string;
  /** Short description for navigation dropdowns */
  description: string;
  /** Icon key for platform feature card (must match Icon component IconType) */
  icon: IconType;
  /** Section grouping for navigation organization */
  section: PlatformSection;
  /** Sort order within section (lower numbers appear first) */
  order: number;
  /** Filter tags for /platform page; features with no tags only show when "All" is selected */
  tags?: PlatformFilterTag[];
  /** Sitemap configuration */
  sitemap?: {
    /** Priority (0.0 to 1.0) */
    priority: string;
    /** Change frequency */
    changefreq: string;
  };
  /** Keys from RELATED_RESOURCES to link to related pages */
  relatedResourceKeys?: (keyof typeof RELATED_RESOURCES)[];
}

/**
 * Central registry of all platform feature pages
 * This is the single source of truth for:
 * - Header navigation (desktop dropdown + mobile menu)
 * - Footer navigation
 * - Sitemap generation
 * - Related resources linking
 *
 * When adding a new platform page:
 * 1. Add entry here with all required fields
 * 2. Create the page file at src/pages/platform/<slug>.tsx
 * 3. Navigation and sitemap will automatically include it
 */
export const PLATFORM_PAGES: PlatformPage[] = [
  // Core Platform section
  {
    label: 'The Creative Workspace',
    href: '/platform/creative-workspace',
    description: 'Unified workspace for creative production',
    icon: 'slides',
    section: 'Core Platform',
    order: 1,
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['projectOrchestration', 'secureAssetStorage'],
  },
  {
    label: 'Review & Approval',
    href: '/platform/review-approval',
    description: 'Video review software for teams and clients',
    icon: 'checkCircle',
    section: 'Core Platform',
    order: 2,
    tags: ['Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['projectOrchestration', 'creativeWorkspace', 'secureAssetStorage'],
  },
  {
    label: 'Project Orchestration',
    href: '/platform/project-orchestration',
    description: 'Tasks & boards on customizable project stages',
    icon: 'board',
    section: 'Core Platform',
    order: 3,
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['creativeWorkspace', 'reviewApproval', 'secureAssetStorage'],
  },
  {
    label: 'Deliverables',
    href: '/platform/deliverables',
    description: 'Track deliverables with deadlines, ownership, and approvals',
    icon: 'calendar',
    section: 'Core Platform',
    order: 3.5,
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['projectOrchestration', 'creativeWorkspace', 'reviewApproval'],
  },
  {
    label: 'Video Annotation',
    href: '/platform/video-annotation',
    description: 'Timecoded video annotation and markup',
    icon: 'monitorPlay',
    section: 'Core Platform',
    order: 4,
    tags: ['Video', 'Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'videoAnnotation'],
  },
  {
    label: 'Draw on a Video',
    href: '/platform/draw-on-a-video',
    description: 'Draw on a video with freehand, shapes, and markup for frame-accurate feedback',
    icon: 'paint',
    section: 'Core Platform',
    order: 6,
    tags: ['Video', 'Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'videoAnnotation'],
  },
  {
    label: 'Comment on Video',
    href: '/platform/comment-on-video',
    description: 'Comment on video with frame-accurate, timestamp-pinned feedback',
    icon: 'reply',
    section: 'Core Platform',
    order: 7,
    tags: ['Video', 'Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'commentOnVideo', 'videoAnnotation'],
  },
  {
    label: 'Add Timestamp to Video',
    href: '/platform/add-timestamp-to-video',
    description: 'Add timestamped comments to video for precise feedback',
    icon: 'time',
    section: 'Core Platform',
    order: 7.5,
    tags: ['Video', 'Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'commentOnVideo', 'videoAnnotation'],
  },
  {
    label: 'Free Video Link Generator',
    href: '/platform/free-video-link-generator',
    description: 'Generate secure video review links for clients and collaborators',
    icon: 'link',
    section: 'Core Platform',
    order: 8,
    tags: ['Video', 'Share & send'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'videoAnnotation', 'secureAssetStorage'],
  },
  {
    label: 'Host Video',
    href: '/platform/host-video',
    description: 'Host video securely with version history, permissions, and frame-accurate review',
    icon: 'monitorPlay',
    section: 'Core Platform',
    order: 9.5,
    tags: ['Video', 'Share & send', 'Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['secureAssetStorage', 'creativeWorkspace', 'reviewApproval', 'videoAnnotation'],
  },
  {
    label: 'Send Video',
    href: '/platform/send-video',
    description: 'Send your video to clients for free review and feedback',
    icon: 'upload',
    section: 'Core Platform',
    order: 10,
    tags: ['Video', 'Share & send'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'videoAnnotation', 'secureAssetStorage'],
  },
  {
    label: 'Embed Video',
    href: '/platform/embed-video',
    description: 'Embed your videos with built-in review and approvals',
    icon: 'fullscreen',
    section: 'Core Platform',
    order: 11,
    tags: ['Video', 'Share & send'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'videoAnnotation', 'secureAssetStorage'],
  },
  {
    label: 'Share MP4',
    href: '/platform/share-mp4',
    description: 'Share MP4 files with clients via secure links for review',
    icon: 'send',
    section: 'Core Platform',
    order: 12,
    tags: ['Video', 'Share & send'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'videoAnnotation', 'secureAssetStorage'],
  },
  {
    label: 'Annotate PDF',
    href: '/platform/annotate-pdf',
    description: 'Annotate and review PDFs with comments and markup',
    icon: 'filePdf',
    section: 'Core Platform',
    order: 13,
    tags: ['PDF & documents', 'Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'guideAddCommentsToPdf', 'freeToolPdfMarkup'],
  },
  {
    label: 'Annotate Image',
    href: '/platform/annotate-image',
    description: 'Annotate images with comments, drawings, and markup for precise feedback',
    icon: 'addImage',
    section: 'Core Platform',
    order: 15,
    tags: ['Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'annotatePdf'],
  },
  {
    label: 'Image Markup',
    href: '/platform/image-markup',
    description: 'Image markup with pins, highlights, and drawing for pixel-accurate feedback',
    icon: 'panorama',
    section: 'Core Platform',
    order: 15.25,
    tags: ['Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'annotatePdf'],
  },
  {
    label: 'Draw on Image',
    href: '/platform/draw-on-image',
    description: 'Draw on images with freehand, shapes, and markup for precise feedback',
    icon: 'panorama',
    section: 'Core Platform',
    order: 16,
    tags: ['Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: [
      'reviewApproval',
      'creativeProofing',
      'annotatePdf',
      'freeToolPdfMarkup',
      'guideAddCommentsToPdf',
    ],
  },
  {
    label: 'Compare PDF Files',
    href: '/platform/compare-pdf-files',
    description: 'Compare two PDF versions side by side with comments and annotations',
    icon: 'compare',
    section: 'Core Platform',
    order: 19,
    tags: ['PDF & documents', 'Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'videoAnnotation', 'commentOnVideo'],
  },
  {
    label: 'Compare Videos',
    href: '/platform/compare-videos',
    description: 'Compare two video versions side by side with frame-accurate comments',
    icon: 'play',
    section: 'Core Platform',
    order: 20,
    tags: ['Video', 'Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'annotatePdf', 'freeToolPdfMarkup'],
  },
  {
    label: 'Compare Documents Online',
    href: '/platform/compare-documents-online',
    description: 'Compare documents online side by side with comments and annotations',
    icon: 'compare',
    section: 'Core Platform',
    order: 21,
    tags: ['PDF & documents', 'Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'annotatePdf', 'guideAddCommentsToPdf'],
  },
  {
    label: 'Download TikTok Video',
    href: '/platform/download-tiktok-video',
    description: 'Download TikTok video from a public link (MP4)',
    icon: 'tiktok',
    section: 'Core Platform',
    order: 28.65,
    tags: ['Video', 'Share & send'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'videoAnnotation', 'secureAssetStorage'],
  },
  {
    label: 'Download Instagram Reels',
    href: '/platform/download-instagram-reels',
    description: 'Download Instagram Reels from a public link (MP4)',
    icon: 'instagram',
    section: 'Core Platform',
    order: 28.66,
    tags: ['Video', 'Share & send'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'videoAnnotation', 'secureAssetStorage'],
  },
  {
    label: 'Download Facebook Reels',
    href: '/platform/download-facebook-reels',
    description: 'Download Facebook Reels from a public link (MP4)',
    icon: 'facebook',
    section: 'Core Platform',
    order: 28.661,
    tags: ['Video', 'Share & send'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'videoAnnotation', 'secureAssetStorage'],
  },
  {
    label: 'Download YouTube Shorts',
    href: '/platform/download-youtube-shorts',
    description: 'Download YouTube Shorts from a public link when a progressive MP4 is available',
    icon: 'youtube',
    section: 'Core Platform',
    order: 28.662,
    tags: ['Video', 'Share & send'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'videoAnnotation', 'secureAssetStorage'],
  },
  {
    label: 'Send Large Video Files',
    href: '/platform/send-large-video-files',
    description: 'Send large video files to clients without email limits',
    icon: 'addVideo',
    section: 'Core Platform',
    order: 29,
    tags: ['Video', 'Share & send'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'videoAnnotation', 'secureAssetStorage'],
  },
  {
    label: 'Video Feedback',
    href: '/platform/video-feedback',
    description: 'Timecoded video feedback with comments and annotations',
    icon: 'reply',
    section: 'Core Platform',
    order: 30,
    tags: ['Video', 'Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'videoAnnotation', 'commentOnVideo'],
  },
  {
    label: 'Video Versioning',
    href: '/platform/video-versioning',
    description: 'Track and compare video versions with version history',
    icon: 'time',
    section: 'Core Platform',
    order: 31,
    tags: ['Video', 'Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'videoAnnotation', 'secureAssetStorage'],
  },
  {
    label: 'PDF Version Control',
    href: '/platform/pdf-version-control',
    description: 'PDF version control with version history and comparison',
    icon: 'filePdf',
    section: 'Core Platform',
    order: 32,
    tags: ['PDF & documents', 'Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'annotatePdf', 'secureAssetStorage'],
  },
  {
    label: 'Document Version Control',
    href: '/platform/document-version-control',
    description: 'Document version control with version history and side-by-side comparison',
    icon: 'fileDoc',
    section: 'Core Platform',
    order: 33,
    tags: ['PDF & documents', 'Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'annotatePdf', 'secureAssetStorage'],
  },
  {
    label: 'Review PDF',
    href: '/platform/review-pdf',
    description: 'Review PDF files with comments, annotations, and approvals',
    icon: 'filePdf',
    section: 'Core Platform',
    order: 37,
    tags: ['PDF & documents', 'Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'annotatePdf', 'guideAddCommentsToPdf'],
  },
  {
    label: 'PDF Proofing Software',
    href: '/platform/proof-pdf',
    description: 'PDF proofing with page comments, markup and client sign-off',
    icon: 'filePdf',
    section: 'Core Platform',
    order: 38,
    tags: ['PDF & documents', 'Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'annotatePdf', 'guideAddCommentsToPdf'],
  },
  {
    label: 'Video Proofing',
    href: '/platform/video-proofing',
    description: 'Video proofing with frame-accurate comments, annotations, and approvals',
    icon: 'checkCircle',
    section: 'Core Platform',
    order: 40,
    tags: ['Video', 'Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'videoAnnotation', 'commentOnVideo'],
  },
  {
    label: 'Manage Videos',
    href: '/platform/manage-videos',
    description: 'Manage videos with version control, feedback tracking, and approvals',
    icon: 'folder',
    section: 'Core Platform',
    order: 41,
    tags: ['Video', 'Review & compare'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'videoAnnotation', 'secureAssetStorage'],
  },
  // Storage & Integrations section
  {
    label: 'Secure Asset Storage',
    href: '/platform/secure-asset-storage',
    description: 'Reliable media storage and organization',
    icon: 'shield',
    section: 'Storage & Integrations',
    order: 1,
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['creativeWorkspace', 'projectOrchestration'],
  },
  {
    label: 'Video Cloud Storage',
    href: '/platform/video-cloud-storage',
    description: 'Video cloud storage with version history and review tools',
    icon: 'monitorPlay',
    section: 'Storage & Integrations',
    order: 1.5,
    tags: ['Video'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['secureAssetStorage', 'creativeWorkspace', 'projectOrchestration'],
  },
  {
    label: 'File Cloud Storage',
    href: '/platform/file-cloud-storage',
    description: 'File cloud storage for all your creative assets',
    icon: 'folder',
    section: 'Storage & Integrations',
    order: 1.75,
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['secureAssetStorage', 'creativeWorkspace', 'projectOrchestration'],
  },
  {
    label: 'Cloud Sharing',
    href: '/platform/cloud-sharing',
    description: 'Cloud sharing for secure, link-based review',
    icon: 'share',
    section: 'Storage & Integrations',
    order: 1.85,
    tags: ['Share & send'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['secureAssetStorage', 'creativeWorkspace', 'reviewApproval'],
  },
  {
    label: 'Cloud File Transfer',
    href: '/platform/cloud-file-transfer',
    description: 'Cloud file transfer links for large creative files',
    icon: 'send',
    section: 'Storage & Integrations',
    order: 1.9,
    tags: ['Share & send'],
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['secureAssetStorage', 'creativeWorkspace', 'reviewApproval'],
  },
  {
    label: 'Integrations',
    href: '/platform/integrations',
    description: 'How Kreatli works with your storage and tools',
    icon: 'gear',
    section: 'Storage & Integrations',
    order: 2,
    sitemap: {
      priority: '0.8',
      changefreq: 'monthly',
    },
    relatedResourceKeys: ['creativeWorkspace', 'secureAssetStorage'],
  },

  // Wave 4 — high-intent category, workflow, and persona pages
  {
    label: 'Online Proofing Software',
    href: '/platform/online-proofing',
    description: 'Online proofing for video, PDF, image and audio review in one workspace',
    icon: 'checkCircle',
    section: 'Core Platform',
    order: 42,
    tags: ['Review & compare'],
    sitemap: { priority: '0.8', changefreq: 'monthly' },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'videoAnnotation'],
  },
  {
    label: 'Artwork Proofing & Approval',
    href: '/platform/artwork-approval',
    description: 'Artwork proofing and approval for print, packaging and label PDFs',
    icon: 'checkCircle',
    section: 'Core Platform',
    order: 42.5,
    tags: ['PDF & documents', 'Review & compare'],
    sitemap: { priority: '0.8', changefreq: 'monthly' },
    relatedResourceKeys: ['printAndPackaging', 'onlineProofing', 'onlineProofingTool'],
  },
  {
    label: 'Video Approval',
    href: '/platform/video-approval',
    description: 'Formal video sign-off with version-pinned approvals and audit trail',
    icon: 'checkCircle',
    section: 'Core Platform',
    order: 44,
    tags: ['Video', 'Review & compare'],
    sitemap: { priority: '0.8', changefreq: 'monthly' },
    relatedResourceKeys: ['reviewApproval', 'creativeProofing', 'commentOnVideo'],
  },
  {
    label: 'Creative Approval Software',
    href: '/platform/creative-approval-workflow',
    description: 'Custom approval statuses and review rounds for creative assets',
    icon: 'board',
    section: 'Core Platform',
    order: 46,
    tags: ['Review & compare'],
    sitemap: { priority: '0.8', changefreq: 'monthly' },
    relatedResourceKeys: ['reviewApproval', 'deliverables', 'projectOrchestration'],
  },
  {
    label: 'Marketing Approval Software',
    href: '/platform/marketing-approval-software',
    description: 'Campaign asset approvals with guest stakeholders and statuses',
    icon: 'checkCircle',
    section: 'Core Platform',
    order: 46.2,
    tags: ['Review & compare'],
    sitemap: { priority: '0.9', changefreq: 'monthly' },
    relatedResourceKeys: ['onlineProofing', 'creativeApprovalWorkflow', 'clientApprovals'],
  },
  {
    label: 'Creative Operations Software',
    href: '/platform/creative-operations-software',
    description: 'Production management and review in one workspace',
    icon: 'board',
    section: 'Core Platform',
    order: 46.4,
    sitemap: { priority: '0.9', changefreq: 'monthly' },
    relatedResourceKeys: ['projectOrchestration', 'deliverables', 'creativeProjectManagement'],
  },
  {
    label: 'Dailies Review',
    href: '/platform/dailies-review',
    description: 'Upload dailies, share with the director, collect frame-pinned notes',
    icon: 'time',
    section: 'Core Platform',
    order: 49,
    tags: ['Video', 'Review & compare'],
    sitemap: { priority: '0.8', changefreq: 'monthly' },
    relatedResourceKeys: ['videoReviewSoftware', 'compareVideos', 'videoAnnotation'],
  },
  {
    label: 'Agency Review Tool',
    href: '/platform/agency-review-tool',
    description: 'Multi-project review and approval for creative agencies',
    icon: 'suitcase',
    section: 'Core Platform',
    order: 50,
    tags: ['Review & compare'],
    sitemap: { priority: '0.8', changefreq: 'monthly' },
    relatedResourceKeys: ['reviewApproval', 'clientApprovals', 'advertisingMarketingAgencies'],
  },
  {
    label: 'Creative Project Management',
    href: '/platform/creative-project-management',
    description: 'Project management built around creative files and review',
    icon: 'board',
    section: 'Core Platform',
    order: 51,
    sitemap: { priority: '0.8', changefreq: 'monthly' },
    relatedResourceKeys: ['projectOrchestration', 'deliverables', 'creativeWorkspace'],
  },
  {
    label: 'Video Asset Management',
    href: '/platform/video-asset-management',
    description: 'Organize video assets by project with review and version control',
    icon: 'folder',
    section: 'Storage & Integrations',
    order: 2.5,
    tags: ['Video'],
    sitemap: { priority: '0.8', changefreq: 'monthly' },
    relatedResourceKeys: ['secureAssetStorage', 'platformManageVideos', 'creativeWorkspace'],
  },
];

/** Max number of platform page links to show in the footer before "All Features" */
export const FOOTER_PLATFORM_PAGES_LIMIT = 15;

/**
 * Get platform pages for footer: first N by order (across all sections).
 * Use with "All Features" link to /platform for the full list.
 */
export function getPlatformPagesForFooter(): PlatformPage[] {
  const sorted = [...PLATFORM_PAGES].sort((a, b) => a.order - b.order);
  return sorted.slice(0, FOOTER_PLATFORM_PAGES_LIMIT);
}

/**
 * Get platform pages grouped by section.
 * Built dynamically from PLATFORM_PAGES so any new page or section is included
 * (e.g. footer and header nav stay in sync automatically).
 * @returns Object with section titles as keys and arrays of pages (sorted by order)
 */
export function getPlatformPagesBySection(): Record<string, PlatformPage[]> {
  const grouped: Record<string, PlatformPage[]> = {};

  PLATFORM_PAGES.forEach((page) => {
    if (!grouped[page.section]) grouped[page.section] = [];
    grouped[page.section].push(page);
  });

  // Sort each section by order
  Object.keys(grouped).forEach((section) => {
    grouped[section].sort((a, b) => a.order - b.order);
  });

  // Sort section keys by min order in section so display order is stable
  const sectionOrder = Object.fromEntries(
    Object.entries(grouped).map(([title, pages]) => [title, Math.min(...pages.map((p) => p.order))]),
  );
  const sortedKeys = Object.keys(grouped).sort((a, b) => sectionOrder[a] - sectionOrder[b]);
  const result: Record<string, PlatformPage[]> = {};
  sortedKeys.forEach((k) => {
    result[k] = grouped[k];
  });
  return result;
}

/**
 * Get a platform page by its href
 * @param href - The href to lookup
 * @returns PlatformPage if found, undefined otherwise
 */
export function getPlatformPageByHref(href: string): PlatformPage | undefined {
  return PLATFORM_PAGES.find((page) => page.href === href);
}

/**
 * Get platform pages formatted for sitemap
 * @returns Array of sitemap entries with path, priority, and changefreq
 */
/** Core commercial pages — higher crawl priority in sitemap */
const PLATFORM_SITEMAP_HIGH_PRIORITY = new Set<string>([
  '/platform/creative-workspace',
  '/platform/review-approval',
  '/platform/marketing-approval-software',
  '/platform/creative-operations-software',
  '/platform/video-annotation',
  '/platform/project-orchestration',
  '/platform/deliverables',
  '/platform/secure-asset-storage',
]);

/** Long-tail / utility feature URLs */
const PLATFORM_SITEMAP_LOW_PRIORITY = new Set<string>([
  '/free-tools/image-url-maker',
  '/platform/share-mp4',
  '/platform/add-timestamp-to-video',
  '/platform/embed-video',
  '/free-tools/pdf-link-generator',
  '/platform/cloud-file-transfer',
  '/platform/cloud-sharing',
  '/platform/video-cloud-storage',
  '/platform/file-cloud-storage',
  '/platform/document-version-control',
  '/platform/pdf-version-control',
  '/platform/video-versioning',
]);

function getPlatformSitemapPriority(href: string): string {
  if (PLATFORM_SITEMAP_HIGH_PRIORITY.has(href)) return '0.9';
  if (PLATFORM_SITEMAP_LOW_PRIORITY.has(href)) return '0.65';
  return '0.75';
}

export function getPlatformPagesForSitemap(): Array<{
  path: string;
  priority: string;
  changefreq: string;
}> {
  return PLATFORM_PAGES.map((page) => ({
    path: page.href,
    priority: getPlatformSitemapPriority(page.href),
    changefreq: page.sitemap?.changefreq || 'monthly',
  }));
}
